import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  IDispositivoLuminariaACTIS,
  TipoDispositivoLorawan,
} from 'modelos/src';
import {
  ICreateDispositivoLorawan,
  IUpdateDispositivoLorawan,
  IDispositivoLorawan,
  IGetDeviceProfileChirpstack,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { DispositivosLorawanService } from '../dispositivos-lorawan.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

interface IDispositivoMasivo {
  deveui: string;
  deviceName: string;
  joineui: string;
  appkey?: string;
  tiempoLimiteComunicacion?: number;
}

interface IValidationError {
  line: number;
  error: string;
}

@Component({
  selector: 'app-crear-editar-dispositivo-lorawan',
  templateUrl: './crear-editar-dispositivo-lorawan.component.html',
  styleUrl: './crear-editar-dispositivo-lorawan.component.scss',
  standalone: false,
})
export class CrearEditarDispositivoLorawanComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CrearEditarDispositivoLorawanComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  private service = inject(DispositivosLorawanService);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);

  public loading = false;
  public clientePropio = LoginService.getCliente();
  public id?: string | null;
  public title?: string;
  public subtitle?: string;
  public dispositivoLorawan?: IDispositivoLorawan;
  public deviceProfiles?: IGetDeviceProfileChirpstack[] = [];

  public tipos: TipoDispositivoLorawan[] = [
    'Luminaria GPE',
    'Luminaria Wellness',
    'Luminaria ACTIS FING',
    'Luminaria CitiLight',
  ];

  public isEditing?: boolean;
  public isSkipFcntCheckReadOnly = false;

  public form?: FormGroup;
  public formOTAA: FormGroup;
  public formABP: FormGroup;
  public formMasivo?: FormGroup;

  public supportsOTAA: boolean | null = null;
  public tipoCliente = HelperService.getTipoCliente();

  // Propiedades para carga masiva
  public modoMasivo = false;
  public csvData = '';
  public validationErrors: IValidationError[] = [];
  public processingMasivo = false;

  private isOTAA(idDeviceProfile: string): boolean | null {
    const device_profile = this.deviceProfiles?.find(
      (d) => d.id === idDeviceProfile,
    );
    return device_profile ? device_profile.supportsOtaa : null;
  }

  get mostrarCamposCliente(): boolean {
    return this.tipoCliente !== 'Final';
  }

  // Alternar entre modo individual y masivo
  public toggleModoMasivo(): void {
    this.modoMasivo = !this.modoMasivo;
    this.csvData = '';
    this.validationErrors = [];

    if (this.modoMasivo) {
      this.createFormMasivo();
    } else {
      this.createForm();
    }
  }

  // Crear formulario para carga masiva
  private createFormMasivo(): void {
    const clienteDefault =
      this.tipoCliente === 'Final' ? this.clientePropio._id : null;

    this.formMasivo = new FormGroup({
      tipo: new FormControl(null, Validators.required),
      idModeloDispositivo: new FormControl(null, Validators.required),
      deviceProfileId: new FormControl(null, Validators.required),
      idCliente: new FormControl(clienteDefault, Validators.required),
      isDisabled: new FormControl(false),
      skipFcntCheck: new FormControl(false),
      tiempoLimiteComunicacion: new FormControl(24, [Validators.min(1)]),
    });

    this.formMasivo
      .get('tipo')
      ?.valueChanges.subscribe((tipoSeleccionado: TipoDispositivoLorawan) => {
        if (tipoSeleccionado === 'Luminaria Wellness') {
          this.formMasivo.get('skipFcntCheck')?.setValue(true);
          this.isSkipFcntCheckReadOnly = true;
        } else {
          this.isSkipFcntCheckReadOnly = false;
        }
      });

    this.formMasivo
      ?.get('deviceProfileId')
      ?.valueChanges.subscribe((deviceProfileId) => {
        this.supportsOTAA = this.isOTAA(deviceProfileId);
      });
  }

  private createForm() {
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : this.dispositivoLorawan?.idCliente;

    this.form = new FormGroup({
      name: new FormControl(this.dispositivoLorawan?.name),
      deveui: new FormControl(this.dispositivoLorawan?.deveui, [
        Validators.required,
        this.hexValidator(16),
      ]),
      joineui: new FormControl(this.dispositivoLorawan?.joineui, [
        Validators.required,
        this.hexValidator(16),
      ]),
      tipo: new FormControl(this.dispositivoLorawan?.tipo, Validators.required),
      idModeloDispositivo: new FormControl(
        this.dispositivoLorawan?.idModeloDispositivo,
        Validators.required,
      ),
      description: new FormControl(this.dispositivoLorawan?.description),
      deviceProfileId: new FormControl(
        this.dispositivoLorawan?.deviceProfileId,
        Validators.required,
      ),
      idCliente: new FormControl(clienteDefault),
      isDisabled: new FormControl(this.dispositivoLorawan?.isDisabled),
      skipFcntCheck: new FormControl(this.dispositivoLorawan?.skipFcntCheck),
      tiempoLimiteComunicacion: new FormControl(
        this.dispositivoLorawan?.tiempoLimiteComunicacion ?? 24,
        [Validators.min(1)],
      ),
    });

    this.form
      .get('tipo')
      ?.valueChanges.subscribe((tipoSeleccionado: TipoDispositivoLorawan) => {
        if (tipoSeleccionado === 'Luminaria Wellness') {
          this.form.get('skipFcntCheck')?.setValue(true);
          this.isSkipFcntCheckReadOnly = true;
        } else {
          this.isSkipFcntCheckReadOnly = false;
        }
      });

    this.updateFormForDeviceProfile();

    this.form
      ?.get('deviceProfileId')
      ?.valueChanges.subscribe((deviceProfileId) => {
        this.supportsOTAA = this.isOTAA(deviceProfileId);
        this.updateFormForDeviceProfile();
      });
  }

  private hexValidator(length: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const hexPattern = /^[0-9a-fA-F]+$/;
      if (value && value.length === length && hexPattern.test(value)) {
        return null;
      }
      return { hexInvalid: true };
    };
  }

  private updateFormForDeviceProfile() {
    if (this.supportsOTAA === null) {
      const deviceProfileId = this.form?.get('deviceProfileId')?.value;
      this.supportsOTAA = this.isOTAA(deviceProfileId);
    }

    if (this.supportsOTAA) {
      this.formOTAA = new FormGroup({
        appkey: new FormControl(this.dispositivoLorawan?.appkey, [
          Validators.required,
          this.hexValidator(32),
        ]),
      });
      this.formABP = null;
    } else {
      this.formABP = new FormGroup({
        devAddr: new FormControl(this.dispositivoLorawan?.devAddr, [
          Validators.required,
          this.hexValidator(8),
        ]),
        appSKey: new FormControl(this.dispositivoLorawan?.appSKey, [
          Validators.required,
          this.hexValidator(32),
        ]),
        fCntUp: new FormControl(this.dispositivoLorawan?.fCntUp),
        nFCntDown: new FormControl(this.dispositivoLorawan?.nFCntDown),
        nwkskey: new FormControl(this.dispositivoLorawan?.nwkskey, [
          Validators.required,
          this.hexValidator(32),
        ]),
      });
      this.formOTAA = null;
    }
  }

  // Validar CSV para carga masiva
  private validateCSV(): IDispositivoMasivo[] | null {
    this.validationErrors = [];
    const lines = this.csvData.trim().split('\n');
    const dispositivos: IDispositivoMasivo[] = [];
    const deveuisUsados = new Set<string>();
    const namesUsados = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const params = line.split(',').map((p) => p.trim());
      const lineNumber = i + 1;

      // Validar cantidad de parámetros
      if (params.length < 2 || params.length > 5) {
        this.validationErrors.push({
          line: lineNumber,
          error:
            'Debe tener entre 2 y 5 parámetros (deveui, deviceName, [joineui], [appkey], [tiempoLimiteComunicacion])',
        });
        continue;
      }

      const [deveui, deviceName, joineui, appkey, tiempoStr] = params;

      // Validar deveui
      if (!deveui || !/^[0-9a-fA-F]{16}$/.test(deveui)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'DevEUI debe ser hexadecimal de 16 caracteres',
        });
        continue;
      }

      if (deveuisUsados.has(deveui.toUpperCase())) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'DevEUI duplicado',
        });
        continue;
      }

      // Validar deviceName
      if (!deviceName) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'DeviceName es obligatorio',
        });
        continue;
      }

      if (namesUsados.has(deviceName)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'DeviceName duplicado',
        });
        continue;
      }

      // Determinar joinEUI
      const finalJoineui = joineui && joineui.length > 0 ? joineui : deveui;

      // Validar joinEUI
      if (!/^[0-9a-fA-F]{16}$/.test(finalJoineui)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'JoinEUI debe ser hexadecimal de 16 caracteres',
        });
        continue;
      }

      // Validar appkey si está presente
      if (appkey && appkey.length > 0 && !/^[0-9a-fA-F]{32}$/.test(appkey)) {
        this.validationErrors.push({
          line: lineNumber,
          error: 'AppKey debe ser hexadecimal de 32 caracteres',
        });
        continue;
      }

      // Validar tiempoLimiteComunicacion si está presente
      let tiempoLimiteComunicacion: number | undefined;
      if (tiempoStr && tiempoStr.length > 0) {
        const tiempoNum = Number(tiempoStr);
        if (isNaN(tiempoNum) || tiempoNum < 1) {
          this.validationErrors.push({
            line: lineNumber,
            error:
              'Tiempo límite de comunicación debe ser un número positivo (en horas)',
          });
          continue;
        }
        tiempoLimiteComunicacion = tiempoNum;
      }

      deveuisUsados.add(deveui.toUpperCase());
      namesUsados.add(deviceName);

      dispositivos.push({
        deveui,
        deviceName,
        joineui: finalJoineui,
        appkey: appkey && appkey.length > 0 ? appkey : undefined,
        tiempoLimiteComunicacion,
      });
    }

    return this.validationErrors.length === 0 ? dispositivos : null;
  }

  // Procesar carga masiva
  public async procesarCargaMasiva(): Promise<void> {
    const dispositivos = this.validateCSV();

    if (!dispositivos) {
      this.helper.notifError('Corrija los errores antes de continuar');
      return;
    }

    const res = await this.dialogService.confirm(
      'Crear Dispositivos Masivamente',
      `¿Querés crear ${dispositivos.length} dispositivo(s)?`,
      'Aceptar',
      'Cancelar',
      true,
      'Crear también en LoRaWAN Server',
      true,
    );

    if (!res.confirmed) return;

    this.processingMasivo = true;
    let creados = 0;
    const lineasProcesadas: number[] = [];

    for (let i = 0; i < dispositivos.length; i++) {
      const dispositivo = dispositivos[i];

      try {
        const create: ICreateDispositivoLorawan = {
          name: dispositivo.deviceName,
          deveui: dispositivo.deveui,
          joineui: dispositivo.joineui,
          tipo: this.formMasivo.value.tipo,
          idModeloDispositivo: this.formMasivo.value.idModeloDispositivo,
          deviceProfileId: this.formMasivo.value.deviceProfileId,
          idCliente: this.formMasivo.value.idCliente,
          isDisabled: this.formMasivo.value.isDisabled,
          skipFcntCheck: this.formMasivo.value.skipFcntCheck,
          tiempoLimiteComunicacion:
            dispositivo.tiempoLimiteComunicacion ??
            this.formMasivo.value.tiempoLimiteComunicacion,
        };

        // Agregar appkey si es OTAA y está presente
        if (this.supportsOTAA && dispositivo.appkey) {
          create.appkey = dispositivo.appkey;
        }

        await this.service.create(create, res.checkbox, true);
        creados++;
        lineasProcesadas.push(i);
      } catch (error) {
        console.error(
          `Error creando dispositivo ${dispositivo.deviceName}:`,
          error,
        );
      }
    }

    // Eliminar líneas procesadas exitosamente del textarea
    if (lineasProcesadas.length > 0) {
      const lines = this.csvData.trim().split('\n');
      const remainingLines = lines.filter(
        (_, index) => !lineasProcesadas.includes(index),
      );
      this.csvData = remainingLines.join('\n');
    }

    this.processingMasivo = false;

    if (creados === dispositivos.length) {
      this.helper.notifSuccess(
        `Se crearon ${creados} de ${dispositivos.length} dispositivos`,
      );
      if (creados === dispositivos.length) {
        this.helper.volver();
      }
    } else if (creados > 0) {
      this.helper.notifSuccess(
        `Se crearon ${creados} de ${dispositivos.length} dispositivos`,
      );
    } else {
      this.helper.notifError('No se crearon dispositivos');
    }
  }

  private async listar(): Promise<void> {
    this.dispositivoLorawan = await firstValueFrom(
      this.lista2.dispositivosLorawan(this.id!),
    );
  }

  private async listarDeviceProfiles() {
    this.deviceProfiles = await firstValueFrom(this.lista2.deviceProfiles());
  }

  public generarRandomHexString(length: number): string {
    const characters = '0123456789ABCDEF';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length),
      );
    }
    return result;
  }

  public ReadOnlyWellness() {
    if (this.isSkipFcntCheckReadOnly) {
      return true;
    } else if (
      this.dispositivoLorawan?.tipo === 'Luminaria Wellness' &&
      this.isEditing
    ) {
      return true;
    } else {
      return false;
    }
  }

  public crearRandomDeveui() {
    const random = this.generarRandomHexString(16);
    this.form.patchValue({ deveui: random });
  }

  public crearRandomJoineui() {
    const random = this.generarRandomHexString(16);
    this.form.patchValue({ joineui: random });
  }

  public crearRandomAppkey() {
    const random = this.generarRandomHexString(32);
    this.formOTAA.patchValue({ appkey: random });
  }

  public crearRandomAppSKey() {
    const random = this.generarRandomHexString(32);
    this.formABP.patchValue({ appSKey: random });
  }

  public crearRandomNwkSKey() {
    const random = this.generarRandomHexString(32);
    this.formABP.patchValue({ nwkskey: random });
  }

  public crearRandomDevAddr() {
    const random = this.generarRandomHexString(8);
    this.formABP.patchValue({ devAddr: random });
  }

  public async onSubmit() {
    if (this.modoMasivo) {
      await this.procesarCargaMasiva();
      return;
    }

    let res: any;

    if (this.id) {
      const confirmed = await this.dialogService.confirm(
        `${this.title}`,
        `¿Querés ${this.title.toLowerCase()}?`,
        'Aceptar',
        'Cancelar',
      );
      if (!confirmed) return;

      try {
        const update = this.getData() as IUpdateDispositivoLorawan;
        await this.service.update(this.id, update);
        this.helper.notifSuccess('Dispositivo Lorawan Editado');
        this.helper.volver();
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
      }
    } else {
      res = await this.dialogService.confirm(
        `${this.title}`,
        `¿Querés ${this.title.toLowerCase()}?`,
        'Aceptar',
        'Cancelar',
        true,
        'Crear también en LoRaWAN Server',
        true,
      );
      if (!res.confirmed) return;

      try {
        const create = this.getData() as ICreateDispositivoLorawan;
        await this.service.create(create, res.checkbox, false);
        this.helper.notifSuccess('Dispositivo Lorawan Creado');
        this.helper.volver();
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
      }
    }
  }

  private getData() {
    const formData = this.form?.value;
    const formABPData = this.formABP?.value;
    const formOTAADate = this.formOTAA?.value;

    const data: ICreateDispositivoLorawan | IUpdateDispositivoLorawan = {
      ...formData,
      ...formABPData,
      ...formOTAADate,
    };

    //Para las luminarias ACTIS FING, hay que armar una configuración inicial cuando se crean ya que no la reportan cuando inicia el nodo, hay que solicitarla
    if (formData.tipo === 'Luminaria ACTIS FING') {
      const config: IDispositivoLuminariaACTIS = {
        iniciarEncendida: true,
        configReportes: {
          reportarEncendidoApagado: true,
          reportarDimerizado: true,
          periodoEstado: 60,
          periodoEnergia: 15,
        },
      };
      data.config = config;
    }

    return data;
  }

  private setTitles() {
    // Si el modulo es 'colectivos' o 'vehiculos' es Editar Chofer
    // El resto es Editar Usuario
    if (this.dispositivoLorawan) {
      this.title = 'Editar dispositivo Lorawan';
      this.subtitle = this.dispositivoLorawan?.name;
    } else {
      this.title = 'Crear dispositivo Lorawan';
    }
  }

  async ngOnInit() {
    this.loading = true;
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    if (this.id) {
      await this.listar();
      this.isEditing = true;
    } else {
      this.isEditing = false;
    }

    await Promise.all([this.listarDeviceProfiles()]);

    this.setTitles();
    this.createForm();
    if (this.tipoCliente === 'Final') {
      this.form.patchValue({
        idCliente: this.clientePropio._id,
      });
    }
    this.loading = false;
  }
}
